import React, { useMemo } from 'react';
import AssignmentChip from './AssignmentChip';
import DayHeader from './DayHeader';
import { Icon } from '@iconify/react';

const MAX_VISIBLE_CHIPS = 2;

const profileImageUrl = (value) => {
  if (!value) return `${process.env.REACT_APP_SPACES_BASE}/user.png`;
  if (String(value).startsWith('http://') || String(value).startsWith('https://')) return value;
  return `${process.env.REACT_APP_SPACES_BASE}/${value}`;
};

const HOURS_PER_DAY_CAPACITY = 12;

const getEventDurationHours = (event) => {
  if (!event) return 0;

  const explicitDuration = Number(event?.original?.duration);
  if (Number.isFinite(explicitDuration) && explicitDuration > 0) {
    return explicitDuration;
  }

  const start = event?.original?.scheduledAt ? new Date(event.original.scheduledAt) : null;
  const end = event?.original?.dueDate ? new Date(event.original.dueDate) : null;
  if (!start || !end || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0;

  const diff = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
  return Number.isFinite(diff) && diff > 0 ? Number(diff.toFixed(2)) : 0;
};

const WorkAssignmentsCalendarGrid = ({
  days,
  assigneeRows,
  eventsByCell,
  collapsedGroups,
  onToggleGroup,
  onCellClick,
  onChipClick,
  onChipRemove,
  onChipDrop,
  gridRef,
}) => {
  const colTemplate = useMemo(() => `240px repeat(${days.length}, minmax(180px, 1fr))`, [days.length]);

  const groupedRows = useMemo(() => ([
    { key: 'internal', label: 'Crew', rows: assigneeRows?.internal || [] },
    { key: 'contractor', label: 'Contractors', rows: assigneeRows?.contractor || [] },
    { key: 'unassigned', label: 'Unassigned Work', rows: assigneeRows?.unassigned || [] },
  ]), [assigneeRows]);

  return (
    <div ref={gridRef} className='border rounded overflow-auto' style={{ height: "calc(100vh - 240px)" }}>
      <div className='d-grid' style={{ gridTemplateColumns: colTemplate, minWidth: '980px' }}>
        <div className='position-sticky top-0 start-0 bg-white border-bottom border-end p-2 fw-semibold z-3'></div>
        {days.map((day) => (
          <div key={day} className='position-sticky top-0 bg-white border-bottom z-2'>
            <DayHeader day={day} />
          </div>
        ))}

        {groupedRows.map((group) => (
          <React.Fragment key={group.key}>
            <div className='position-sticky start-0 bg-light border-end border-bottom p-0 z-1'>
              <button
                type='button'
                className='btn btn-link text-decoration-none w-100 text-start px-2 py-1 d-flex align-items-center'
                onClick={() => onToggleGroup?.(group.key)}
              >
                <span className='fw-semibold small p-8'>{group.label}</span>
                <span className='text-muted ms-2 small'>({group.rows.length})</span>
                <Icon
                  icon={collapsedGroups?.[group.key] ? 'mdi:chevron-down' : 'mdi:chevron-up'}
                  className='ms-auto me-8 text-muted text-xl'
                  aria-hidden='true'
                />
              </button>
            </div>
            {days.map((day) => (
              <div key={`${group.key}-header-${day}`} className='border-bottom border-end bg-light' />
            ))}

            {!collapsedGroups?.[group.key] && group.rows.map((assignee) => (
              <React.Fragment key={`${assignee.type}-${assignee.id}`}>
                <div className='position-sticky start-0 bg-white border-end border-bottom p-8 pt-16 small z-1 d-flex align-items-start gap-2'>
                  {assignee.type === 'unassigned' ? (
                    <div
                      className='rounded-circle d-flex align-items-center justify-content-center bg-light flex-shrink-0'
                      style={{ height: '35px', width: '35px' }}
                    >
                      <Icon icon='mdi:clipboard-text-outline' className='text-muted' />
                    </div>
                  ) : (
                    <img
                      src={profileImageUrl(assignee.profileImage)}
                      alt={assignee.name}
                      width='25'
                      height='25'
                      className='rounded-circle object-fit-cover flex-shrink-0'
                      style={{ height: "35px", width: "35px" }}
                    />
                  )}
                  <div>
                    <div className='fw-semibold ms-6 text-black' style={{ position: "relative", top: "-2px" }}>{assignee.name}</div>
                    {assignee.secondaryName ? <div className='small text-muted ms-6'>{assignee.secondaryName}</div> : null}
                  </div>
                </div>
                {days.map((day) => {
                  const cellKey = `${assignee.type}:${assignee.id}:${day}`;
                  const events = eventsByCell[cellKey] || [];
                  const totalHours = events.reduce((sum, event) => sum + getEventDurationHours(event), 0);
                  const progressPercent = Math.min(100, (totalHours / HOURS_PER_DAY_CAPACITY) * 100);

                  return (
                    <div
                      key={cellKey}
                      className='border-bottom border-end p-1 align-top position-relative'
                      style={{ minHeight: '120px', height: '160px', cursor: 'pointer', paddingLeft: '14px' }}
                      onClick={() => onCellClick?.({ day, assignee })}
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = 'move';
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        const payload = e.dataTransfer.getData('application/json');
                        if (!payload) return;
                        try {
                          const dragEvent = JSON.parse(payload);
                          onChipDrop?.({ dragEvent, day, assignee });
                        } catch (_error) {
                          // Ignore invalid payloads
                        }
                      }}
                    >
                      <div
                        className='position-absolute top-0 start-0 bottom-0 d-flex align-items-end'
                        style={{ width: '8px', margin: '2px 0 2px 2px' }}
                      >
                      </div>
                      {(events[0]?.assignees.length > 0 ) && (<div className="progress progress-sm mb-8">
                        <div
                          className="progress-bar bg-primary-400"
                          style={{
                            width: `${progressPercent}%`,
                            ariaValuenow: {progressPercent},
                            ariaValuemin: "0",
                            ariValuemax: "100",
                          }}
                          title={`${totalHours.toFixed(1)}h / ${HOURS_PER_DAY_CAPACITY}h`} />
                      </div>)}
                      {events.slice(0, MAX_VISIBLE_CHIPS).map((event) => (
                        <div key={event.id} onClick={(e) => e.stopPropagation()}>
                          <AssignmentChip
                            event={event}
                            onClick={onChipClick}
                            onRemove={assignee.type === 'unassigned' ? undefined : () => onChipRemove?.({ event, assignee })}
                            draggable
                            onDragStart={(payload) => {
                              payload.dataTransfer.setData('application/json', JSON.stringify(event));
                            }}
                          />
                        </div>
                      ))}
                      {events.length > MAX_VISIBLE_CHIPS && (
                        <details onClick={(e) => e.stopPropagation()}>
                          <summary className='small text-primary'>+{events.length - MAX_VISIBLE_CHIPS} more</summary>
                          <div className='mt-1'>
                            {events.slice(MAX_VISIBLE_CHIPS).map((event) => (
                              <AssignmentChip
                                key={`${event.id}-more`}
                                event={event}
                                onClick={onChipClick}
                                onRemove={assignee.type === 'unassigned' ? undefined : () => onChipRemove?.({ event, assignee })}
                                draggable
                                onDragStart={(payload) => {
                                  payload.dataTransfer.setData('application/json', JSON.stringify(event));
                                }}
                              />
                            ))}
                          </div>
                        </details>
                      )}
                    </div>
                  );
                })}
              </React.Fragment>
            ))}

            {!collapsedGroups?.[group.key] && group.rows.length === 0 && (
              <>
                <div className='position-sticky start-0 bg-white border-end border-bottom p-8 small text-muted z-1'>No assignees</div>
                {days.map((day) => (
                  <div key={`${group.key}-empty-${day}`} className='border-bottom border-end bg-white' />
                ))}
              </>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default WorkAssignmentsCalendarGrid;
